import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, MapPin, Bus, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function RoutesPage() {
  const { company } = useAuth();
  const currency = company?.currency || "FCFA";
  const [tab, setTab] = useState("routes");
  const [cities, setCities] = useState([]);
  const [routes, setRoutes] = useState([]);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/cities"), api.get("/routes")]);
      setCities(c.data);
      setRoutes(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Trajets & Villes</h1>
          <p className="text-sm text-slate-500">Gérez vos itinéraires, prix et horaires</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="routes" data-testid="tab-routes">Trajets</TabsTrigger>
          <TabsTrigger value="cities" data-testid="tab-cities">Villes</TabsTrigger>
        </TabsList>

        <TabsContent value="routes" className="space-y-4">
          <NewRouteDialog cities={cities} onCreated={load} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {routes.map((r) => (
              <Card key={r.id} data-testid={`route-card-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-orange-500 font-semibold">
                    <Bus className="w-3.5 h-3.5" /> Trajet
                  </div>
                  <div className="font-display font-bold text-lg mt-2">{r.from_city} → {r.to_city}</div>
                  <div className="text-xs text-slate-500">{r.to_region}</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-display font-bold text-2xl text-orange-500">{formatCurrency(r.price, currency)}</span>
                    <span className="text-xs text-slate-500">/billet</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">Horaires : {r.schedules?.join(", ") || "—"}</div>
                  <div className="text-xs text-slate-500">Places : {r.seats}</div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" onClick={async () => {
                      if (!confirm("Supprimer ce trajet ?")) return;
                      await api.delete(`/routes/${r.id}`); toast.success("Trajet supprimé"); load();
                    }} data-testid={`delete-route-${r.id}`}><Trash2 className="w-4 h-4 mr-1" /> Supprimer</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {routes.length === 0 && <div className="text-slate-500 col-span-full text-center py-8">Aucun trajet — ajoutez le premier !</div>}
          </div>
        </TabsContent>

        <TabsContent value="cities" className="space-y-4">
          <NewCityDialog onCreated={load} />
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
                  <tr><th className="text-left p-3">Ville</th><th className="text-left">Région</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {cities.map((c) => (
                    <tr key={c.id}>
                      <td className="p-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-500" /> {c.name}</td>
                      <td>{c.region || "—"}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="icon" onClick={async () => {
                          if (!confirm("Supprimer cette ville ?")) return;
                          await api.delete(`/cities/${c.id}`); toast.success("Ville supprimée"); load();
                        }} data-testid={`delete-city-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                  {cities.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">Aucune ville</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NewCityDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-city-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle ville</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouvelle ville</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-city-name" /></div>
          <div><Label>Région</Label><Input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="new-city-region" /></div>
          <Button onClick={async () => {
            try { await api.post("/cities", { name, region }); toast.success("Ville ajoutée"); setOpen(false); setName(""); setRegion(""); onCreated(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }} className="btn-pill bg-orange-500 hover:bg-orange-600 w-full" data-testid="save-city-btn">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewRouteDialog({ cities, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_city_id: "", to_city_id: "", price: "", duration_min: "", schedules: "", seats: 50, active: true });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-route-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau trajet</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouveau trajet</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Départ</Label>
            <Select value={form.from_city_id} onValueChange={(v) => setForm({ ...form, from_city_id: v })}>
              <SelectTrigger data-testid="new-route-from"><SelectValue placeholder="Ville" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Destination</Label>
            <Select value={form.to_city_id} onValueChange={(v) => setForm({ ...form, to_city_id: v })}>
              <SelectTrigger data-testid="new-route-to"><SelectValue placeholder="Ville" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Prix</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="new-route-price" /></div>
          <div><Label>Places</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></div>
          <div className="col-span-2"><Label>Horaires (séparés par virgule)</Label><Input value={form.schedules} onChange={(e) => setForm({ ...form, schedules: e.target.value })} placeholder="06:00, 08:00, 12:00" data-testid="new-route-schedules" /></div>
        </div>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full mt-3" onClick={async () => {
          try {
            await api.post("/routes", { ...form, price: Number(form.price), seats: Number(form.seats), duration_min: Number(form.duration_min || 0), schedules: form.schedules.split(",").map((s) => s.trim()).filter(Boolean) });
            toast.success("Trajet créé"); setOpen(false); onCreated();
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }} data-testid="save-route-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}
